import { Component, OnInit } from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import {DmConnectApiService, WorkflowResult} from '../dm-connect-api.service';
import {GoogleAnalyticsService} from 'ngx-google-analytics';
import {DmconnectBrowserService} from '../dmconnect-browser.service';
import {catchError, mergeMap, timeout} from 'rxjs/operators';
import {Observable, of, TimeoutError} from 'rxjs';
import {WorkflowService} from '../workflows/workflow-service.service';
import {WorkflowResponseHandler} from '../workflows/workflow-response-handler.service';

@Component({
    selector: 'app-aim-screens',
    template: `
      <app-loading-indicator loading_text="Preparing your submission for markets..." [is_loading]="!correlationId"></app-loading-indicator>
      <ng-container *ngIf="correlationId">
        <p>This submission has already been sent to ClarionDoor.<span *ngIf="enablePull"> Would you like to bring the indication into AIM?</span></p>
        <div class="dmc-button-panel">
          <button type="button" class="btn btn-primary me-1" *ngIf="enablePull" (click)="pullIndication()" [disabled]="request_loading">
            <ng-container *ngIf="request_loading; else pullReady">
              <span class="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
              Loading...
            </ng-container>
            <ng-template #pullReady>Yes</ng-template>
          </button>
          <button type="button" class="btn btn-light" (click)="continueWorking()">
            <ng-container *ngIf="enablePull; else continue">
                No, continue working in ClarionDoor
            </ng-container>
            <ng-template #continue>Continue working in ClarionDoor</ng-template>
          </button>
        </div>
      </ng-container>
  `,
    styles: [],
    standalone: false
})
export class AimClarionDoorComponent implements OnInit {
  private clarionDoorHost: string;
  private submissionId: string;
  private dataSource: string;
  private quoteId: string;
  private version: string;
  private policyId: string;
  private userId: string;
  public correlationId: string;
  public enablePull: boolean;
  public error_message: string;
  public request_loading: boolean;

  constructor(private route: ActivatedRoute,  private dmConnectApiService: DmConnectApiService,
              private browserService: DmconnectBrowserService, private googleAnalytics: GoogleAnalyticsService,
              private workflowService: WorkflowService, private router: Router, private handler: WorkflowResponseHandler) {
    const routeData = route.snapshot;
    this.submissionId = routeData.queryParamMap.get('submission_id') || routeData.queryParamMap.get('PolicyID');
    this.quoteId = routeData.queryParamMap.get('quote_id');
    this.version = routeData.queryParamMap.get('version');
    this.policyId = routeData.queryParamMap.get('policy_id') || routeData.queryParamMap.get('PolicyID');
    this.userId = routeData.queryParamMap.get('user_id') || routeData.queryParamMap.get('EmployeeID');
    this.dataSource = routeData.queryParamMap.get('data_source');
    this.clarionDoorHost = routeData.queryParamMap.get('clarion_door_host');
    this.enablePull = this.isPullEnabled(routeData.queryParamMap.get('enable_pull'));

    if (!this.submissionId) {
      this.handler.handle('status', {error: {
        workflow_id: 'SUBMISSION_ID_MISSING',
        message: 'A submission ID was not included in the request.'
      }});
    } else {
      this.dmConnectApiService.getStatus2('cd_mgahub', this.submissionId)
      .pipe(
        timeout(10000),
        mergeMap(result => this.handleStatusResponse(result, this)),
        mergeMap(wasHandled => {
          if (wasHandled) {
            return of(true);
          }
          return this.handler.handle('status', {error: {
            workflow_id: 'STATUS_REQUEST',
            message: 'An error was encountered while requesting DmConnect status.'
          }});
        }),
        catchError(err => {
          return this.handler.handle('status', {error: {
              workflow_id: 'STATUS_REQUEST',
              message: err instanceof TimeoutError
                ? 'The request timed out while requesting DmConnect status.'
                : 'An error was encountered while requesting DmConnect status.'
            }});
        })
      ).subscribe();
    }
  }

  ngOnInit(): void {
  }

  private handleStatusResponse(result, thisComponent: this): Observable<boolean> {
    if (!result || !result.success) {
      return of(false);
    }

    thisComponent.correlationId = result.correlation_id;
    this.enablePull = this.isPullEnabled(this.enablePull ?? result?.enable_pull);
    this.clarionDoorHost = this.clarionDoorHost ?? result?.clarion_door_host;

    if (thisComponent.correlationId) {
      return of(true);
    } else {
      return thisComponent.workflowService.execute('outbound', {
        submission_id: thisComponent.submissionId,
        quote_id: thisComponent.quoteId,
        version: thisComponent.version,
        policy_id: thisComponent.policyId,
        user_id: thisComponent.userId,
        data_source: thisComponent.dataSource,
        dm_portal_id: 'cd_mgahub'
      }).pipe(mergeMap<WorkflowResult, Observable<boolean>>(r2 => {

        if (r2 && r2.output && r2.output.open_browser) {
          thisComponent.browserService.openFullBrowser(r2.output.open_browser);
          return of(true);
        }
        if (r2.success || r2.output.error){
          return of (true);
        }
        return of(false);
      }));
    }

    return of(false);
  }


  public isPullEnabled(enablePull: string): boolean {
    return enablePull && String(enablePull).toLowerCase() === 'true';
  }

  public pullIndication(): void {
    this.request_loading = true;
    this.googleAnalytics.event('pulled_cdmgahub_indication', 'clariondoor', 'Pulled indication from CDMGAHub');
    this.workflowService.execute('inbound', { id: this.correlationId, dm_target_id: this.submissionId, dm_portal_id: 'cd_mgahub', portal_id: 'cd_mgahub' }).
    subscribe(() => this.browserService.closeWindow());
  }

  public continueWorking(): void {
    this.googleAnalytics.event('continue_working_cdmgahub', 'clariondoor', 'Continuing work in CDMGAHub');
    this.browserService.openFullBrowser(this.clarionDoorHost + this.correlationId);
  }
}
